/**
 * Job Listing Slider
 * - Prev / Next buttons
 * - Dot navigation
 * - Drag-to-scroll via Pointer Events (works for mouse, trackpad, touch & pen —
 *   covers desktop click-drag and phone/tablet touch swipe with one code path)
 * - Recalculates slides-per-view on resize (desktop / tablet / mobile breakpoints
 *   come from CSS custom properties set inline by the Liquid section)
 */
(function () {
  'use strict';

  function initSlider(root) {
    var viewport = root.querySelector('[data-jc-viewport]');
    var track = root.querySelector('[data-jc-track]');
    var prevBtn = root.querySelector('[data-jc-prev]');
    var nextBtn = root.querySelector('[data-jc-next]');
    var dotsWrap = root.querySelector('[data-jc-dots]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-jc-card]'));

    prevBtn.style.display = 'none';
    nextBtn.style.display = 'none';

    if (!track || slides.length < 1) return;

    prevBtn.style.display = 'flex';
    nextBtn.style.display = 'flex';

    var isDown = false;
    var dragged = false;
    var startX = 0;
    var startScrollLeft = 0;
    var pointerId = null;

    // ---------- helpers ----------

    function getSlideStep() {
      // Width of one slide including the gap, used for button + keyboard paging
      var slide = slides[0];
      var slideRect = slide.getBoundingClientRect();
      var trackStyles = window.getComputedStyle(track);
      var gap = parseFloat(trackStyles.columnGap || trackStyles.gap || 0) || 0;
      return slideRect.width + gap;
    }

    function getSlidesPerView() {
      var trackWidth = track.clientWidth;
      var step = getSlideStep();
      if (!step) return 1;
      return Math.max(1, Math.round(trackWidth / step));
    }

    function getPageCount() {
      var perView = getSlidesPerView();
      return Math.max(1, Math.ceil(slides.length / perView));
    }

    function getCurrentPage() {
      var step = getSlideStep() * getSlidesPerView();
      if (!step) return 0;
      return Math.round(track.scrollLeft / step);
    }

    function scrollToPage(page) {
      var pageCount = getPageCount();
      page = Math.max(0, Math.min(page, pageCount - 1));
      var step = getSlideStep() * getSlidesPerView();
      track.scrollTo({ left: page * step, behavior: 'smooth' });
    }

    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      var maxScroll = track.scrollWidth - track.clientWidth - 1;
      prevBtn.disabled = track.scrollLeft <= 0;
      nextBtn.disabled = track.scrollLeft >= maxScroll;
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      var pageCount = getPageCount();
      if (pageCount <= 1) {
        dotsWrap.hidden = true;
        return;
      }
      dotsWrap.hidden = false;
      for (var i = 0; i < pageCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'jc-slider__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        (function (page) {
          dot.addEventListener('click', function () {
            scrollToPage(page);
          });
        })(i);
        dotsWrap.appendChild(dot);
      }
      updateDots();
    }

    function updateDots() {
      if (!dotsWrap) return;
      var current = getCurrentPage();
      var dots = dotsWrap.querySelectorAll('.jc-slider__dot');
      dots.forEach(function (dot, i) {
        if (i === current) {
          dot.setAttribute('data-active', '');
        } else {
          dot.removeAttribute('data-active');
        }
      });
    }

    var scrollRaf = null;
    function onScroll() {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(function () {
        updateArrows();
        updateDots();
      });
    }

    // ---------- buttons ----------

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollToPage(getCurrentPage() - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollToPage(getCurrentPage() + 1);
      });
    }

    // ---------- keyboard (when a slide inside the track is focused) ----------

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollToPage(getCurrentPage() + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollToPage(getCurrentPage() - 1);
      }
    });

    // ---------- drag / swipe (Pointer Events: mouse, touch, pen) ----------

    var DRAG_THRESHOLD = 6; // px of movement before we treat this as a drag, not a tap/click

    track.addEventListener('pointerdown', function (e) {
      // Only primary button for mouse; touch/pen always primary
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      isDown = true;
      dragged = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
      // IMPORTANT: do NOT capture the pointer here. Capturing immediately
      // retargets the eventual "click" event to `track` instead of the
      // card underneath the finger/cursor, which silently breaks the
      // card's own click listener (the one that opens the popup) even
      // for a plain tap with zero movement. Capture is deferred to
      // pointermove, once we've confirmed the user is actually dragging.
    });

    track.addEventListener('pointermove', function (e) {
      if (!isDown || e.pointerId !== pointerId) return;
      var deltaX = e.clientX - startX;

      if (!dragged && Math.abs(deltaX) > DRAG_THRESHOLD) {
        dragged = true;
        track.setAttribute('data-dragging', '');
        // Now that this is a real drag, capture the pointer so we keep
        // receiving move/up events even if the cursor leaves the track.
        try {
          track.setPointerCapture(pointerId);
        } catch (err) {
          /* noop */
        }
      }

      if (dragged) {
        e.preventDefault();
        track.scrollLeft = startScrollLeft - deltaX;
      }
    });

    function endDrag(e) {
      if (!isDown) return;
      isDown = false;
      track.removeAttribute('data-dragging');

      if (dragged && pointerId !== null) {
        try {
          track.releasePointerCapture(pointerId);
        } catch (err) {
          /* noop */
        }
        // Snap to the nearest page after a free drag
        scrollToPage(getCurrentPage());
      }
      pointerId = null;

      // Reset the flag on the next tick so the click handler on the card
      // (bound in job-listing.js) can check it before it clears — a plain
      // tap never sets `dragged`, so its click fires and opens the modal
      // normally.
      window.setTimeout(function () {
        dragged = false;
      }, 0);
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', function (e) {
      if (isDown) endDrag(e);
    });

    // Prevent the browser's native image/text drag ghost from interfering
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    // ---------- scroll + resize ----------

    track.addEventListener('scroll', onScroll, { passive: true });

    var resizeRaf = null;
    window.addEventListener('resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        buildDots();
        updateArrows();
      });
    });

    // ---------- init ----------

    buildDots();
    updateArrows();
  }

  function init() {
    document.querySelectorAll('[data-jc-slider]').forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when sections are re-rendered in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var slider = e.target.querySelector && e.target.querySelector('[data-jc-slider]');
    if (slider) initSlider(slider);
  });
})();